import {
  ZIP64_UINT16_SENTINEL,
  ZIP64_UINT32_SENTINEL,
  ZIP_EOCD_RECORD_BYTES,
  ZIP_EOCD_SEARCH_WINDOW_BYTES,
  ZipStructureResult,
} from "./definitions";

const readUint16LE = (bytes: Uint8Array, offset: number): number =>
  bytes[offset] | (bytes[offset + 1] << 8);

/** The high byte is multiplied, not shifted, to stay unsigned. */
const readUint32LE = (bytes: Uint8Array, offset: number): number =>
  (bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16)) +
  bytes[offset + 3] * 0x1000000;

/**
 * Decides whether an EOCD-signature hit is a real EOCD, by checking that the
 * central directory it points at is actually present. Mirrors
 * `zipfile._RealGetContents`, which is what decides whether the import
 * succeeds.
 */
const isEocdBackedByCentralDirectory = async (
  file: File,
  tail: Uint8Array,
  tailStart: number,
  offsetInTail: number,
): Promise<boolean> => {
  const eocdOffset = tailStart + offsetInTail;
  const totalEntries = readUint16LE(tail, offsetInTail + 10);
  const centralDirectorySize = readUint32LE(tail, offsetInTail + 12);
  const centralDirectoryOffset = readUint32LE(tail, offsetInTail + 16);

  // ZIP64 keeps the real sizes/offsets in a record we do not parse. Accept
  // rather than reject: `zipfile` reads them and opens the archive.
  if (
    totalEntries === ZIP64_UINT16_SENTINEL ||
    centralDirectorySize === ZIP64_UINT32_SENTINEL ||
    centralDirectoryOffset === ZIP64_UINT32_SENTINEL
  ) {
    return true;
  }

  if (centralDirectorySize === 0) {
    return totalEntries === 0;
  }

  // `zipfile`'s own `concat` prefix correction, applied unguarded as it does.
  const prefixBytes =
    eocdOffset - centralDirectorySize - centralDirectoryOffset;
  const centralDirectoryStart = centralDirectoryOffset + prefixBytes;
  if (centralDirectoryStart < 0) {
    return false;
  }
  if (centralDirectoryStart + 4 > file.size) {
    return false;
  }

  const signature =
    centralDirectoryStart >= tailStart
      ? tail.subarray(
          centralDirectoryStart - tailStart,
          centralDirectoryStart - tailStart + 4,
        )
      : new Uint8Array(
          await file
            .slice(centralDirectoryStart, centralDirectoryStart + 4)
            .arrayBuffer(),
        );

  return (
    signature[0] === 0x50 &&
    signature[1] === 0x4b &&
    signature[2] === 0x01 &&
    signature[3] === 0x02
  );
};

/**
 * Cheap pre-upload structural check for a Team Files import ZIP: rejects an
 * archive the downstream `zipfile.ZipFile()` cannot open. Reads at most the
 * first 4 bytes plus the final `ZIP_EOCD_SEARCH_WINDOW_BYTES`.
 */
export const validateZipStructure = async (
  file: File,
): Promise<ZipStructureResult> => {
  /**
   * Picks the user-facing reason once the archive is known to be bad.
   */
  const classifyFailure = async (): Promise<ZipStructureResult> => {
    const head = new Uint8Array(await file.slice(0, 4).arrayBuffer());
    const looksLikeZip =
      head[0] === 0x50 &&
      head[1] === 0x4b &&
      ((head[2] === 0x03 && head[3] === 0x04) ||
        (head[2] === 0x05 && head[3] === 0x06));
    return { valid: false, reason: looksLikeZip ? "truncated" : "not-a-zip" };
  };

  // Classified on 2 signature bytes, not 4: the rest may have been truncated.
  if (file.size < ZIP_EOCD_RECORD_BYTES) {
    const lead = new Uint8Array(await file.slice(0, 2).arrayBuffer());
    const startsWithPk = lead[0] === 0x50 && lead[1] === 0x4b;
    return { valid: false, reason: startsWithPk ? "truncated" : "not-a-zip" };
  }

  const tailStart = Math.max(0, file.size - ZIP_EOCD_SEARCH_WINDOW_BYTES);
  const tail = new Uint8Array(
    await file.slice(tailStart, file.size).arrayBuffer(),
  );

  // Scan backwards and validate every candidate: keep scanning past a failed
  // one so a stray signature nearer EOF cannot mask the real EOCD behind it.
  for (let i = tail.length - ZIP_EOCD_RECORD_BYTES; i >= 0; i--) {
    if (
      tail[i] === 0x50 &&
      tail[i + 1] === 0x4b &&
      tail[i + 2] === 0x05 &&
      tail[i + 3] === 0x06
    ) {
      if (await isEocdBackedByCentralDirectory(file, tail, tailStart, i)) {
        return { valid: true };
      }
    }
  }

  return classifyFailure();
};
